import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { ProductGenerationDocumentRef } from '@flows-ui/services/QuoteDataContext.types'
import type { CoveragePlan, ProductKey } from '@flows/index'
import { getProductParamPath } from '@flows/insurance/utils'

export const useCoveragePlanSelection = (props: {
  productKey: ProductKey
  coveragePlans: CoveragePlan[]
  /** Sanity/CMS `defaultCoveragePlan` reference; form is synced when no plan matches yet */
  defaultCoveragePlan?: ProductGenerationDocumentRef | null
}) => {
  const { watch, setValue } = useFormContext()
  const { trackPriceChange } = useQuoteData()
  const values = watch()
  const trackPriceChangeRef = useRef(trackPriceChange)
  trackPriceChangeRef.current = trackPriceChange

  const hasAppliedDefaultPlan = useRef(false)
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null)

  const productConfig = values?.products?.[props.productKey]?.configuration_parameters as
    | Record<string, unknown>
    | undefined

  const selectedPlanFromKey = useMemo(() => {
    if (!selectedPlanKey) {
      return undefined
    }
    return props.coveragePlans.find(
      (plan) => (plan.itemKey?.current ?? plan._id) === selectedPlanKey
    )
  }, [props.coveragePlans, selectedPlanKey])

  const selectedPlanFromConfig = useMemo(() => {
    const matchingPlans = props.coveragePlans.filter((plan) =>
      // Loose match: form often has numeric deductible (0) while CMS uses string ("0").
      plan.configurationParameterMap.every(
        (map) => String(productConfig?.[map.parameterName]) === String(map.parameterValue)
      )
    )

    if (!matchingPlans.length) {
      return undefined
    }
    // Prefer the most specific match to avoid being stuck on broad/default mappings.
    return matchingPlans.sort(
      (a, b) => b.configurationParameterMap.length - a.configurationParameterMap.length
    )[0]
  }, [props.coveragePlans, productConfig])

  const selectedPlan = selectedPlanFromKey ?? selectedPlanFromConfig

  useEffect(() => {
    if (hasAppliedDefaultPlan.current) {
      return
    }
    if (!props.coveragePlans?.length) {
      return
    }

    if (selectedPlan) {
      hasAppliedDefaultPlan.current = true
      return
    }

    const defaultRef = props.defaultCoveragePlan?._ref
    const plan =
      (defaultRef && props.coveragePlans.find((p) => p._id === defaultRef)) ??
      props.coveragePlans[0]

    if (plan) {
      plan.configurationParameterMap.forEach((map) => {
        setValue(getProductParamPath(props.productKey, map.parameterName), map.parameterValue, {
          shouldValidate: true,
        })
      })
      setSelectedPlanKey(plan.itemKey?.current ?? plan._id)
      hasAppliedDefaultPlan.current = true
      trackPriceChangeRef.current('coverage_plan_default')
    }
  }, [
    props.coveragePlans,
    props.defaultCoveragePlan?._ref,
    props.productKey,
    selectedPlan,
    setValue,
  ])

  const selectCoveragePlan = (plan: CoveragePlan) => {
    setSelectedPlanKey(plan.itemKey?.current ?? plan._id)
    plan.configurationParameterMap.forEach((map) => {
      setValue(getProductParamPath(props.productKey, map.parameterName), map.parameterValue, {
        shouldValidate: true,
      })
    })
    trackPriceChange('coverage_plan_change')
  }

  const configurationParameters = productConfig

  return {
    selectedPlan,
    configurationParameters,
    selectCoveragePlan,
  }
}
